import { 
  User, 
  Company, 
  Plan, 
  Subscription, 
  Invoice, 
  Ticket, 
  MOCK_USERS, 
  MOCK_COMPANIES, 
  MOCK_PLANS, 
  MOCK_SUBSCRIPTIONS, 
  MOCK_INVOICES, 
  MOCK_TICKETS 
} from '../types';

const STORAGE_KEYS = {
  USERS: 'ethics_users',
  COMPANIES: 'ethics_companies',
  PLANS: 'ethics_plans',
  SUBSCRIPTIONS: 'ethics_subscriptions',
  INVOICES: 'ethics_invoices',
  TICKETS: 'ethics_tickets',
  CURRENT_USER: 'ethics_current_user',
  ONBOARDING_DATA: 'ethics_onboarding_data'
};

// Initialize storage with mock data if empty
export const initializeStorage = () => {
  if (!localStorage.getItem(STORAGE_KEYS.USERS)) {
    localStorage.setItem(STORAGE_KEYS.USERS, JSON.stringify(MOCK_USERS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.COMPANIES)) {
    localStorage.setItem(STORAGE_KEYS.COMPANIES, JSON.stringify(MOCK_COMPANIES));
  }
  // Only initialize plans if not exists - allows admin edits to persist
  if (!localStorage.getItem(STORAGE_KEYS.PLANS)) {
    localStorage.setItem(STORAGE_KEYS.PLANS, JSON.stringify(MOCK_PLANS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.SUBSCRIPTIONS)) {
    localStorage.setItem(STORAGE_KEYS.SUBSCRIPTIONS, JSON.stringify(MOCK_SUBSCRIPTIONS));
  }
  if (!localStorage.getItem(STORAGE_KEYS.INVOICES)) {
    localStorage.setItem(STORAGE_KEYS.INVOICES, JSON.stringify(MOCK_INVOICES));
  }
  if (!localStorage.getItem(STORAGE_KEYS.TICKETS)) {
    localStorage.setItem(STORAGE_KEYS.TICKETS, JSON.stringify(MOCK_TICKETS));
  }
};

// Generic CRUD operations
const getItems = <T>(key: string): T[] => {
  const data = localStorage.getItem(key);
  return data ? JSON.parse(data) : [];
};

const setItems = <T>(key: string, items: T[]): void => {
  localStorage.setItem(key, JSON.stringify(items));
};

// Users
export const getUsers = (): User[] => getItems<User>(STORAGE_KEYS.USERS);
export const getUserById = (id: string): User | undefined => {
  return getUsers().find(u => u.id === id);
};
export const getUserByEmail = (email: string): User | undefined => {
  return getUsers().find(u => u.email === email);
};
export const createUser = (user: User): User => {
  const users = getUsers();
  users.push(user);
  setItems(STORAGE_KEYS.USERS, users);
  return user;
};
export const updateUser = (id: string, data: Partial<User>): User | null => {
  const users = getUsers();
  const index = users.findIndex(u => u.id === id);
  if (index >= 0) {
    users[index] = { ...users[index], ...data };
    setItems(STORAGE_KEYS.USERS, users);
    return users[index];
  }
  return null;
};
export const deleteUser = (id: string): boolean => {
  const users = getUsers();
  const filtered = users.filter(u => u.id !== id);
  setItems(STORAGE_KEYS.USERS, filtered);
  return filtered.length < users.length;
};

// Companies
export const getCompanies = (): Company[] => getItems<Company>(STORAGE_KEYS.COMPANIES);
export const getCompanyById = (id: string): Company | undefined => {
  return getCompanies().find(c => c.id === id);
};
export const getCompanyByCnpj = (cnpj: string): Company | undefined => {
  return getCompanies().find(c => c.cnpj === cnpj);
};
export const createCompany = (company: Company): Company => {
  const companies = getCompanies();
  companies.push(company);
  setItems(STORAGE_KEYS.COMPANIES, companies);
  return company;
};
export const updateCompany = (id: string, data: Partial<Company>): Company | null => {
  const companies = getCompanies();
  const index = companies.findIndex(c => c.id === id);
  if (index >= 0) {
    companies[index] = { ...companies[index], ...data };
    setItems(STORAGE_KEYS.COMPANIES, companies);
    return companies[index];
  }
  return null;
};

// Plans
export const getPlans = (): Plan[] => getItems<Plan>(STORAGE_KEYS.PLANS);
export const getPlanById = (id: string): Plan | undefined => {
  return getPlans().find(p => p.id === id);
};
export const updatePlan = (id: string, data: Partial<Plan>): Plan | null => {
  const plans = getPlans();
  const index = plans.findIndex(p => p.id === id);
  if (index >= 0) {
    plans[index] = { ...plans[index], ...data };
    setItems(STORAGE_KEYS.PLANS, plans);
    return plans[index];
  }
  return null;
};
export const createPlan = (plan: Plan): Plan => {
  const plans = getPlans();
  plans.push(plan);
  setItems(STORAGE_KEYS.PLANS, plans);
  return plan;
};
export const deletePlan = (id: string): boolean => {
  const plans = getPlans();
  const filtered = plans.filter(p => p.id !== id);
  setItems(STORAGE_KEYS.PLANS, filtered);
  return filtered.length < plans.length;
};

// Subscriptions
export const getSubscriptions = (): Subscription[] => getItems<Subscription>(STORAGE_KEYS.SUBSCRIPTIONS);
export const getSubscriptionById = (id: string): Subscription | undefined => {
  return getSubscriptions().find(s => s.id === id);
};
export const getSubscriptionByCompanyId = (companyId: string): Subscription | undefined => {
  return getSubscriptions().find(s => s.companyId === companyId);
};
export const createSubscription = (subscription: Subscription): Subscription => {
  const subscriptions = getSubscriptions();
  subscriptions.push(subscription);
  setItems(STORAGE_KEYS.SUBSCRIPTIONS, subscriptions);
  return subscription;
};
export const updateSubscription = (id: string, data: Partial<Subscription>): Subscription | null => {
  const subscriptions = getSubscriptions();
  const index = subscriptions.findIndex(s => s.id === id);
  if (index >= 0) {
    subscriptions[index] = { ...subscriptions[index], ...data };
    setItems(STORAGE_KEYS.SUBSCRIPTIONS, subscriptions);
    return subscriptions[index];
  }
  return null;
};

// Invoices
export const getInvoices = (): Invoice[] => getItems<Invoice>(STORAGE_KEYS.INVOICES);
export const getInvoicesByCompanyId = (companyId: string): Invoice[] => {
  return getInvoices().filter(i => i.companyId === companyId);
};
export const createInvoice = (invoice: Invoice): Invoice => {
  const invoices = getInvoices();
  invoices.push(invoice);
  setItems(STORAGE_KEYS.INVOICES, invoices);
  return invoice;
};
export const updateInvoice = (id: string, data: Partial<Invoice>): Invoice | null => {
  const invoices = getInvoices();
  const index = invoices.findIndex(i => i.id === id);
  if (index >= 0) {
    invoices[index] = { ...invoices[index], ...data };
    setItems(STORAGE_KEYS.INVOICES, invoices);
    return invoices[index];
  }
  return null;
};

// Tickets
export const getTickets = (): Ticket[] => getItems<Ticket>(STORAGE_KEYS.TICKETS);
export const getTicketsByCompanyId = (companyId: string): Ticket[] => {
  return getTickets().filter(t => t.companyId === companyId);
};
export const createTicket = (ticket: Ticket): Ticket => {
  const tickets = getTickets();
  tickets.push(ticket);
  setItems(STORAGE_KEYS.TICKETS, tickets);
  return ticket;
};
export const updateTicket = (id: string, data: Partial<Ticket>): Ticket | null => {
  const tickets = getTickets();
  const index = tickets.findIndex(t => t.id === id);
  if (index >= 0) {
    tickets[index] = { ...tickets[index], ...data };
    setItems(STORAGE_KEYS.TICKETS, tickets);
    return tickets[index];
  }
  return null;
};

// Current User Session
export const getCurrentUser = (): User | null => {
  const data = localStorage.getItem(STORAGE_KEYS.CURRENT_USER);
  return data ? JSON.parse(data) : null;
};
export const setCurrentUser = (user: User | null): void => {
  if (user) {
    localStorage.setItem(STORAGE_KEYS.CURRENT_USER, JSON.stringify(user));
  } else {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_USER);
  }
};

// Onboarding Data (temporary storage during signup)
export const getOnboardingData = (): Record<string, any> | null => {
  const data = localStorage.getItem(STORAGE_KEYS.ONBOARDING_DATA);
  return data ? JSON.parse(data) : null;
};
export const setOnboardingData = (data: Record<string, any>): void => {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_DATA, JSON.stringify(data));
};
export const clearOnboardingData = (): void => {
  localStorage.removeItem(STORAGE_KEYS.ONBOARDING_DATA);
};

// Auth helpers
export const authenticateUser = (email: string, password: string): User | null => {
  // Simple mock authentication - in real app, compare hashed passwords
  const user = getUserByEmail(email);
  if (user && password === '123456') { // Mock password for all users
    setCurrentUser(user);
    return user;
  }
  return null;
};

export const logoutUser = (): void => {
  setCurrentUser(null);
};

// Reset all data (for testing)
export const resetAllData = (): void => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  initializeStorage();
};
